import { defineStore } from "pinia";
import api from "../services/api";
import { useGameStore } from "./game";

function asList(value) {
  return Array.isArray(value) ? value : [];
}

function errorMessage(error, fallback) {
  return (
    error.response?.data?.message ||
    error.response?.data?.error ||
    fallback
  );
}

export const useGuildStore = defineStore("guild", {
  state: () => ({
    membership: null,
    myCharter: null,
    charters: [],
    charterPage: 1,
    charterTotalPages: 1,
    charterLimit: 10,
    guilds: [],
    guildPage: 1,
    guildTotalPages: 1,
    guildLimit: 20,
    selectedCharter: null,
    selectedGuild: null,
    isLoading: false,
    loadError: "",
    lastUnusedGold: 0,
  }),
  getters: {
    isMember: (state) => Boolean(state.membership),
    myRole: (state) => state.membership?.role || state.membership?.my_role || null,
    canModerate(state) {
      const role = state.membership?.role || state.membership?.my_role;
      return role === "leader" || role === "officer";
    },
  },
  actions: {
    applyResources(payload) {
      if (!payload?.resources) return;
      useGameStore().mergeEmpireStocks(payload.resources);
    },

    rememberUnused(payload) {
      const unused = Number(payload?.unused) || 0;
      this.lastUnusedGold = unused > 0 ? unused : 0;
      return this.lastUnusedGold;
    },

    patchCharter(charter) {
      if (!charter?.id) return;
      const id = Number(charter.id);
      this.charters = this.charters.map((row) =>
        Number(row.id) === id
          ? {
              ...row,
              ...charter,
              signers: charter.signers || row.signers,
            }
          : row
      );
      if (this.myCharter && Number(this.myCharter.id) === id) {
        this.myCharter = { ...this.myCharter, ...charter };
      }
      if (this.selectedCharter && Number(this.selectedCharter.id) === id) {
        this.selectedCharter = charter;
      }
    },

    async fetchMy() {
      const response = await api.get("/api/guilds/my");
      this.membership = response.data?.membership || null;
      this.myCharter = response.data?.charter || null;
      if (this.membership) {
        this.selectedGuild = this.membership;
        this.myCharter = null;
      }
      return response.data;
    },

    async fetchCharters({ page, search, limit } = {}) {
      this.isLoading = true;
      this.loadError = "";
      const params = {
        page: page ?? this.charterPage,
        limit: limit ?? this.charterLimit,
      };
      const q = String(search || "").trim();
      if (q) params.search = q;
      try {
        const response = await api.get("/api/guilds/charters", { params });
        this.charters = asList(response.data?.data ?? response.data?.charters);
        this.charterPage = Number(response.data?.currentPage) || 1;
        this.charterTotalPages = Math.max(
          1,
          Number(response.data?.totalPages) || 1
        );
        return this.charters;
      } catch (error) {
        this.charters = [];
        this.charterPage = 1;
        this.charterTotalPages = 1;
        this.loadError = errorMessage(error, "Could not load founding charters.");
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchGuilds({ page, search, limit } = {}) {
      this.isLoading = true;
      this.loadError = "";
      const params = {
        page: page ?? this.guildPage,
        limit: limit ?? this.guildLimit,
      };
      const q = String(search || "").trim();
      if (q) params.search = q;
      try {
        const response = await api.get("/api/guilds/list", { params });
        this.guilds = asList(response.data?.data);
        this.guildPage = Number(response.data?.currentPage) || 1;
        this.guildTotalPages = Math.max(
          1,
          Number(response.data?.totalPages) || 1
        );
        return this.guilds;
      } catch (error) {
        this.guilds = [];
        this.guildPage = 1;
        this.guildTotalPages = 1;
        this.loadError = errorMessage(error, "Could not load the guild directory.");
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchCharter(charterId) {
      const response = await api.get(`/api/guilds/charter/${charterId}`);
      this.selectedCharter = response.data?.charter || null;
      return this.selectedCharter;
    },

    async fetchGuild(guildId) {
      const response = await api.get(`/api/guilds/${guildId}`);
      this.selectedGuild = response.data?.guild || null;
      return this.selectedGuild;
    },

    async createCharter({ name, description, gold }) {
      const response = await api.post("/api/guilds/charter/create", {
        name,
        description,
        gold,
      });
      this.applyResources(response.data);
      this.rememberUnused(response.data);
      this.myCharter = response.data?.charter || null;
      this.selectedCharter = this.myCharter;
      await this.fetchMy();
      return response.data;
    },

    async signCharter(charterId, { pledge } = {}) {
      const body = {};
      if (pledge != null && pledge !== "") body.pledge = pledge;
      const response = await api.post(`/api/guilds/charter/${charterId}/sign`, body);
      this.applyResources(response.data);
      this.rememberUnused(response.data);
      this.selectedCharter = response.data?.charter || this.selectedCharter;
      this.patchCharter(this.selectedCharter);
      await this.fetchMy();
      await this.fetchCharters();
      return response.data;
    },

    async resolveSigner({ charterId, userId, action }) {
      const response = await api.put(
        `/api/guilds/charter/${charterId}/signers/${userId}`,
        { action }
      );
      this.selectedCharter = response.data?.charter || this.selectedCharter;
      this.patchCharter(this.selectedCharter);
      return response.data;
    },

    async contribute({ charterId, gold }) {
      const response = await api.post(
        `/api/guilds/charter/${charterId}/contribute`,
        { gold }
      );
      this.applyResources(response.data);
      this.rememberUnused(response.data);
      this.patchCharter(response.data?.charter);
      return response.data;
    },

    async foundCharter(charterId) {
      const response = await api.post(`/api/guilds/charter/${charterId}/found`);
      this.membership = response.data?.membership || response.data?.guild || null;
      this.myCharter = null;
      this.selectedCharter = null;
      await this.fetchMy();
      return response.data;
    },

    async cancelCharter(charterId) {
      const response = await api.post(`/api/guilds/charter/${charterId}/cancel`);
      this.applyResources(response.data);
      this.myCharter = null;
      this.selectedCharter = null;
      await this.fetchMy();
      await this.fetchCharters();
      return response.data;
    },

    async requestJoin(guildId) {
      const response = await api.post(`/api/guilds/${guildId}/join`);
      if (this.selectedGuild && Number(this.selectedGuild.id) === Number(guildId)) {
        this.selectedGuild = {
          ...this.selectedGuild,
          my_join_status: "pending",
        };
      }
      return response.data;
    },

    async resolveJoin({ guildId, userId, action }) {
      const response = await api.put(`/api/guilds/${guildId}/join/${userId}`, {
        action,
      });
      this.selectedGuild = response.data?.guild || this.selectedGuild;
      if (this.membership && Number(this.membership.id) === Number(guildId)) {
        this.membership = this.selectedGuild
          ? { ...this.selectedGuild, role: this.membership.role }
          : this.membership;
      }
      return response.data;
    },

    async leaveGuild() {
      const response = await api.post("/api/guilds/leave");
      this.membership = null;
      this.selectedGuild = null;
      await this.fetchMy();
      await this.fetchGuilds({ page: 1 });
      return response.data;
    },

    async kickMember({ guildId, targetUserId }) {
      const response = await api.delete(
        `/api/guilds/${guildId}/members/${targetUserId}`
      );
      this.selectedGuild = response.data?.guild || this.selectedGuild;
      if (this.membership && Number(this.membership.id) === Number(guildId)) {
        this.membership = this.selectedGuild
          ? { ...this.selectedGuild, role: this.membership.role }
          : this.membership;
      }
      return response.data;
    },
  },
});
